import { createConsoleLogger, LogLevels, PowerSyncDatabase } from '@powersync/web';

import {
  clearSearchIndex,
  ensureSearchIndex,
  flushSearchIndex,
  FlowSyncConnector,
  queries,
  syncSchema,
} from '@flow/sync';

import {
  API_URL,
  APP_VERSION,
  DEBUG_SQL,
  MULTI_TABS,
  POWERSYNC_URL,
  PREFERRED_VFS,
  SEARCH_INDEX,
  WEB_WORKER,
} from '../config.js';

import { chooseVfs, type VfsChoice } from './vfs.js';

/**
 * Local database of the signed-in user (docs/07 §3, §4.3, §7): one SQLite file per user,
 * PowerSync schema and connector shared with mobile (`packages/sync`), sync JWT and uploads
 * through the API with the session cookie.
 */

export interface LocalDatabase {
  userId: string;
  db: PowerSyncDatabase;
  connector: FlowSyncConnector;
  vfs: VfsChoice;
  /** Result of `navigator.storage.persist()`: granted, denied or unsupported (null). */
  persisted: boolean | null;
  /** Epoch ms when the database was opened (first-sync measurement, docs/12 §4). */
  openedAt: number;
  /** ms since the page started (`performance.now()`) when the database was ready to query. */
  readyMs: number;
  /**
   * Whether this database had already received data from the server when it was opened. The SDK
   * status (`hasSynced`) is only known a moment later, and reading it at mount made the test UI
   * show «first sync» on every cold start of the installed iPhone app (f0-mobile-pwa).
   */
  hadSynced: boolean;
}

export function databaseFilename(userId: string): string {
  return `flow-${userId}.db`;
}

/** Asks the browser to keep this origin's storage; Safari only grants it to installed apps. */
export async function requestPersistentStorage(): Promise<boolean | null> {
  if (typeof navigator === 'undefined' || !navigator.storage?.persist) return null;
  try {
    if (await navigator.storage.persisted()) return true;
    return await navigator.storage.persist();
  } catch {
    return null;
  }
}

export async function openLocalDatabase(userId: string): Promise<LocalDatabase> {
  const openedAt = Date.now();
  const vfs = await chooseVfs(PREFERRED_VFS);
  const db = new PowerSyncDatabase({
    schema: syncSchema,
    database: {
      dbFilename: databaseFilename(userId),
      vfs: vfs.vfs,
      ...(MULTI_TABS === undefined ? {} : { enableMultiTabs: MULTI_TABS }),
      ...(WEB_WORKER === undefined ? {} : { useWebWorker: WEB_WORKER }),
      ...(DEBUG_SQL ? { debugMode: true } : {}),
    },
    logger: createConsoleLogger({ prefix: 'PowerSync', minLevel: LogLevels.warn }),
  });
  await db.init();
  const hadQuery = queries.hasServerData();
  const hadSynced =
    (await db.getOptional<{ had: number }>(hadQuery.sql, hadQuery.params))?.had === 1;
  // Full-text index and its triggers before any row arrives, so synced rows are recorded; the
  // index itself is updated in batch (after the first sync here, and before every search).
  const searchIndex = SEARCH_INDEX ? await ensureSearchIndex(db) : null;
  if (searchIndex) {
    db.waitForFirstSync()
      .then(() => flushSearchIndex(db))
      .catch((error: unknown) => console.warn('[flow] índice de búsqueda', error));
  }

  const connector = new FlowSyncConnector({
    apiUrl: API_URL,
    ...(POWERSYNC_URL ? { powersyncUrl: POWERSYNC_URL } : {}),
    platform: 'web',
    appVersion: APP_VERSION,
    fetch: (input, init) => fetch(input, { ...init, credentials: 'include' }),
  });
  // Not awaited: in browsers without multi-tab support (Safari) `connect()` waits for a lock held
  // by the tab that is syncing, and the local data must be usable meanwhile.
  db.connect(connector).catch((error: unknown) => {
    console.warn('[flow] no se ha podido conectar con el servicio de sincronización', error);
  });
  const persisted = await requestPersistentStorage();
  console.info(
    `[flow] base local ${databaseFilename(userId)} · VFS ${vfs.name}${vfs.fallback ? ' (memoria, sin persistencia)' : ''} · multiTabs: ${String(MULTI_TABS ?? 'sdk')} · persist(): ${String(persisted)} · búsqueda ${searchIndex ? `v${searchIndex.version}${searchIndex.rebuilt ? ' (reconstruida)' : ''}` : 'desactivada'}`,
  );
  return {
    userId,
    db,
    connector,
    vfs,
    persisted,
    openedAt,
    readyMs: Math.round(performance.now()),
    hadSynced,
  };
}

/** Stops syncing and removes the user's local data (sign-out, docs/10). */
export async function closeLocalDatabase(local: LocalDatabase): Promise<void> {
  await local.db.disconnectAndClear();
  // The SDK only clears its own tables: the local search index holds titles and bodies too.
  await clearSearchIndex(local.db);
  await local.db.close();
}
